import { ReactNode } from 'react'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Tab } from '@mui/material'
import { LoadingIndicator } from '@ui/feedback'
import { tabConfig } from './constants'

type Tabs = keyof typeof tabConfig

interface Props {
  isLoading?: boolean
  assemblyManager: ReactNode
  connectionManager: ReactNode
  shearConnectionManager: ReactNode
  defaultCrossSectionsManager: ReactNode
  // Projektkonfiguration
  projectFieldsManager?: ReactNode
  // Only rendered in the global system manager, not in the per-project one
  plansDocumentationManager?: ReactNode
  // Only rendered for users with the KI feature (access:ai)
  aiInstructionsManager?: ReactNode
  activeTab?: Tabs
  setActiveTab: (tab: Tabs) => void
}

const ManagerTabs = ({
  isLoading = false,
  assemblyManager,
  connectionManager,
  shearConnectionManager,
  defaultCrossSectionsManager,
  projectFieldsManager,
  plansDocumentationManager,
  aiInstructionsManager,
  activeTab,
  setActiveTab,
}: Props) => {
  if (isLoading) {
    return <LoadingIndicator />
  }

  // An optional tab that isn't rendered (no manager given, e.g. while the KI permission is still
  // loading) must not be selected: TabContext would point at a missing tab and show nothing.
  const optionalManagers: Partial<Record<Tabs, ReactNode>> = {
    projectFields: projectFieldsManager,
    plansDocumentation: plansDocumentationManager,
    aiInstructions: aiInstructionsManager,
  }
  const isUnavailable = !!activeTab && activeTab in optionalManagers && !optionalManagers[activeTab]
  const value = activeTab && !isUnavailable ? activeTab : tabConfig.assemblies.value

  return (
    <TabContext value={value}>
      {/* Scrollable once the tabs no longer fit. `centered` doesn't combine with
          that, so the row is centred by CSS -- `safe` falls back to the start
          when it overflows, keeping the first tab reachable. */}
      <TabList
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ '& .MuiTabs-flexContainer': { justifyContent: 'safe center' } }}
      >
        <Tab
          {...tabConfig.assemblies}
          sx={{ backgroundColor: '#fff', borderTopLeftRadius: 8 }}
          data-cy="tab-assemblies"
        />
        <Tab
          {...tabConfig.connections}
          sx={{ backgroundColor: '#fff' }}
          data-cy="tab-connections"
        />
        <Tab
          {...tabConfig.shearConnections}
          sx={{ backgroundColor: '#fff' }}
          data-cy="tab-shear-connections"
        />
        <Tab
          {...tabConfig.defaultCrossSections}
          sx={{ backgroundColor: '#fff' }}
          data-cy="tab-default-cross-sections"
        />
        {projectFieldsManager && (
          <Tab
            {...tabConfig.projectFields}
            sx={{ backgroundColor: '#fff' }}
            data-cy="tab-project-fields"
          />
        )}
        {plansDocumentationManager && (
          <Tab
            {...tabConfig.plansDocumentation}
            sx={{ backgroundColor: '#fff' }}
            data-cy="tab-plans-documentation"
          />
        )}
        {aiInstructionsManager && (
          <Tab
            {...tabConfig.aiInstructions}
            sx={{ backgroundColor: '#fff' }}
            data-cy="tab-ai-instructions"
          />
        )}
      </TabList>
      <TabPanel value={tabConfig.assemblies.value}>{assemblyManager}</TabPanel>
      <TabPanel value={tabConfig.connections.value}>{connectionManager}</TabPanel>
      <TabPanel value={tabConfig.shearConnections.value}>{shearConnectionManager}</TabPanel>
      <TabPanel value={tabConfig.defaultCrossSections.value}>
        {defaultCrossSectionsManager}
      </TabPanel>
      {projectFieldsManager && (
        <TabPanel value={tabConfig.projectFields.value}>{projectFieldsManager}</TabPanel>
      )}
      {plansDocumentationManager && (
        <TabPanel value={tabConfig.plansDocumentation.value}>{plansDocumentationManager}</TabPanel>
      )}
      {aiInstructionsManager && (
        <TabPanel value={tabConfig.aiInstructions.value}>{aiInstructionsManager}</TabPanel>
      )}
    </TabContext>
  )
}

export default ManagerTabs
